import { useEffect, useState } from 'react';
import { Form, Input, InputNumber, Select, DatePicker, Card, Typography, Button, Modal, Row, Col, Tag, message } from '../../ui';
import { Plus } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';

const TYPES = [
  { value: 'SLAB_DISCOUNT', label: 'Slab Discount' },
  { value: 'BUNDLE', label: 'Buy X Get Y Free' },
  { value: 'CASHBACK', label: 'Cashback' },
];

export function SchemesPage() {
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['schemes'],
    queryFn: () => request<any[]>({ url: '/schemes' }),
  });

  const columns: DataTableColumn<any>[] = [
    { key: 'code', title: 'Code', dataIndex: 'code', sortable: true, alwaysVisible: true },
    { key: 'name', title: 'Name', dataIndex: 'name', sortable: true, alwaysVisible: true },
    {
      key: 'type', title: 'Type', dataIndex: 'type', sortable: true,
      render: (v: string) => <Tag tone="blue">{TYPES.find((t) => t.value === v)?.label ?? v}</Tag>,
    },
    {
      key: 'rules', title: 'Rules', dataIndex: 'rules',
      render: (r: any, row: any) => {
        if (row.type === 'SLAB_DISCOUNT') {
          return (r?.slabs ?? []).map((s: any, i: number) =>
            <Tag key={i}>≥{s.minQty} → {s.discountPct}%</Tag>
          );
        }
        if (row.type === 'BUNDLE') {
          return <Tag tone="green">Buy {r?.buyQty} get {r?.freeQty} free</Tag>;
        }
        return <Tag>{JSON.stringify(r)}</Tag>;
      },
    },
    { key: 'validFrom', title: 'From', dataIndex: 'validFrom', sortable: true },
    { key: 'validTo', title: 'To', dataIndex: 'validTo', render: (v: any) => v ?? '—' },
    {
      key: 'isActive', title: 'Active', dataIndex: 'isActive', sortable: true,
      render: (v: boolean) => v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>,
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Schemes &amp; Discounts</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setOpen(true)}>
            New scheme
          </Button>
        </div>
      </div>
      <Card>
        <DataTable
          storageKey="schemes"
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>
      <SchemeFormModal open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function SchemeFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const [type, setType] = useState('SLAB_DISCOUNT');

  useEffect(() => {
    if (open) {
      form.resetFields();
      // Form.List is name="slabs" (top-level) and the mutation reads vals.slabs,
      // so seed the defaults there — nesting under `rules` left the editor empty.
      form.setFieldsValue({ type: 'SLAB_DISCOUNT', validFrom: dayjs(), isActive: true,
        slabs: [{ minQty: 10, discountPct: 2 }, { minQty: 50, discountPct: 5 }] });
      setType('SLAB_DISCOUNT');
    }
  }, [open, form]);

  const save = useMutation({
    mutationFn: (vals: any) => {
      let rules: any;
      if (vals.type === 'SLAB_DISCOUNT') {
        rules = { slabs: (vals.slabs ?? []).filter((s: any) => s.minQty != null) };
      } else if (vals.type === 'BUNDLE') {
        rules = { buyQty: vals.buyQty, freeQty: vals.freeQty };
      } else {
        rules = { minAmount: vals.minAmount, cashbackPct: vals.cashbackPct };
      }
      const payload = {
        code: vals.code,
        name: vals.name,
        type: vals.type,
        stateCode: vals.stateCode,
        productId: vals.productId,
        rules,
        validFrom: vals.validFrom?.format('YYYY-MM-DD'),
        validTo: vals.validTo?.format('YYYY-MM-DD'),
        isActive: vals.isActive,
      };
      return request({ url: '/schemes', method: 'POST', data: payload });
    },
    onSuccess: () => {
      message.success('Created');
      qc.invalidateQueries({ queryKey: ['schemes'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="New scheme" width={680}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Create</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="code" label="Code" rules={[{ required: true }]}>
              <Input placeholder="SLAB_NPK_Q4" />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
              <Input placeholder="NPK Quarter-end slab discount" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="type" label="Scheme type" rules={[{ required: true }]}>
              <Select options={TYPES} onChange={(v) => setType(v)} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="stateCode" label="Applies to state (optional)">
              <Input maxLength={4} placeholder="27 = MH (blank = all)" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="productId" label="Specific product (optional)">
              <Input placeholder="product ID, blank = all" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="validFrom" label="Valid from" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="validTo" label="Valid to">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        {type === 'SLAB_DISCOUNT' && (
          <Form.List name="slabs">
            {(fields, { add, remove }) => (
              <div>
                <Typography.Text strong>Quantity slabs</Typography.Text>
                {fields.map(({ key, name }) => (
                  <Row key={key} gutter={8} style={{ marginTop: 8 }}>
                    <Col span={10}>
                      <Form.Item name={[name, 'minQty']} noStyle>
                        <InputNumber placeholder="Min qty" style={{ width: '100%' }} min={1} />
                      </Form.Item>
                    </Col>
                    <Col span={10}>
                      <Form.Item name={[name, 'discountPct']} noStyle>
                        <InputNumber placeholder="Discount %" style={{ width: '100%' }} min={0} max={50} step={0.5} />
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <Button variant="danger" onClick={() => remove(name)}>Remove</Button>
                    </Col>
                  </Row>
                ))}
                <Button variant="default" onClick={() => add({ minQty: 1, discountPct: 0 })} className="mt-2" block>
                  Add slab
                </Button>
              </div>
            )}
          </Form.List>
        )}

        {type === 'BUNDLE' && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="buyQty" label="Buy quantity" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="freeQty" label="Free quantity" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {type === 'CASHBACK' && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="minAmount" label="Minimum amount (₹)" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} step={1000} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cashbackPct" label="Cashback %" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} min={0} max={50} step={0.5} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
}
